import { isPresent, isWinning } from './derivePricingResolution';
import type {
    BorrowStatusKey,
    PoolPricingConfig,
    PoolPricingSimulationResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';

/** Mirrors the backend's `roundPrice`: prices round to 4 decimal places. */
function roundPrice(price: number): number {
    return Math.round(price * 10000 + 1e-15) / 10000;
}

function floorFor(config: PoolPricingConfig, borrowStatus: BorrowStatusKey | null): number | null {
    if (!config.minimumPrice?.enabled || borrowStatus === null) return null;
    const floor = config.minimumPrice.prices[borrowStatus];
    return isPresent(floor) ? floor : null;
}

/**
 * Locally re-resolves a simulation against a DRAFT config, reusing the
 * server-provided market inputs (candidates, borrow status) from a
 * saved-config run — candidates are config-independent, so a reader-scoped
 * pool simulation supplies everything an unsaved-edit preview needs
 * without the superuser-only arbitrary-config endpoint.
 *
 * Mirrors the backend's `resolvePoolPrice` — multiplier before floor,
 * floors only usable (finite, non-negative) values, result rounded to 4
 * decimals — with the UI's one deliberate divergence: a zero candidate
 * never wins (see {@link derivePricingResolution}'s design note).
 */
export function previewDraftSimulation(
    config: PoolPricingConfig,
    base: PoolPricingSimulationResponse,
): PoolPricingSimulationResponse {
    const winner = (config.hierarchy ?? []).map((method) => base.candidates[method]).find(isWinning);
    const floor = floorFor(config, base.borrowStatus);
    let price: number | null = winner ?? null;
    if (
        price !== null &&
        config.multiplier?.enabled &&
        typeof config.multiplier.multiplier === 'number' &&
        config.multiplier.multiplier > 0
    ) {
        price *= config.multiplier.multiplier;
    }
    if (price !== null && floor !== null && price < floor) price = floor;
    return {
        symbol: base.symbol,
        config,
        candidates: base.candidates,
        borrowStatus: base.borrowStatus,
        price: price === null ? null : roundPrice(price),
        minimumPrice: floor,
        // The UI cannot edit quoteVolume, so the draft's factor is always
        // the saved one the server already computed.
        quoteVolumeFactor: base.quoteVolumeFactor,
    };
}
